import APIService from "./APIService";

const SimulationScheduleAPI = {
  /**
   * Trigger simulation schedule creation (like EventBridge Lambda)
   * Calls backend without parameters - backend handles all logic
   * @returns {Promise} - Promise that resolves to the API response
   */
  createSchedule: async () => {
    return APIService.post("/api/v1/simulation/create-schedule");
  },

  /**
   * Get all simulation schedules
   * @param {Object} params - Optional parameters for filtering
   * @param {string} params.userUuid - Filter by user UUID
   * @param {string} params.status - Filter by status
   * @param {number} params.page - Page number for pagination
   * @param {number} params.size - Page size for pagination
   * @returns {Promise} - Promise that resolves to the API response with schedules data
   */
  getSchedules: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.userUuid) {
      queryParams.append("userUuid", params.userUuid);
    }
    if (params.status) {
      queryParams.append("status", params.status);
    }
    if (params.page !== undefined) {
      queryParams.append("page", params.page);
    }
    if (params.size !== undefined) {
      queryParams.append("size", params.size);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/api/v1/simulation/schedules?${queryString}`
      : "/api/v1/simulation/schedules";

    return APIService.get(endpoint);
  },

  /**
   * Execute a specific simulation schedule
   * @param {Object} executeData - The execution data containing scheduleId
   * @returns {Promise} - Promise that resolves to the API response
   */
  executeSchedule: async (executeData) => {
    return APIService.post("/api/v1/simulation/execute-schedule", executeData);
  },

  /**
   * Execute all pending simulation schedules
   * @param {Object} options - Optional execution parameters (batchSize, forceBatch, etc.)
   * @returns {Promise} - Promise that resolves to the API response
   */
  executeAllSchedules: async (options = {}) => {
    return APIService.post("/api/v1/simulation/execute-schedule", {
      executeBatch: true,
      ...options,
    });
  },

  /**
   * Delete a simulation schedule
   * @param {number} scheduleId - The ID of the schedule to delete
   * @returns {Promise} - Promise that resolves to the API response
   */
  deleteSchedule: async (scheduleId) => {
    return APIService.delete(`/api/v1/simulation/schedule/${scheduleId}`);
  },

  /**
   * Get pause status for a user's simulation schedule
   * @param {string} userUuid - The UUID of the user
   * @returns {Promise} - Promise that resolves to pause status data
   */
  getPauseStatus: async (userUuid) => {
    return APIService.get(
      `/api/v1/simulation/schedule/pause-status/${userUuid}`
    );
  },

  /**
   * Pause simulation schedule
   * @param {string} userUuid - The UUID of the user
   * @param {Object} pauseData - Pause configuration
   * @param {string} pauseData.pauseOption - Type: 'indefinite', 'until-date', or 'duration'
   * @param {string} pauseData.endDate - End date for 'until-date' option (ISO string)
   * @param {number} pauseData.durationDays - Duration in days for 'duration' option
   * @param {string} pauseData.reason - Optional reason for pause
   * @returns {Promise} - Promise that resolves to the API response
   */
  pauseSchedule: async (userUuid, pauseData) => {
    return APIService.post(`/api/v1/simulation/schedule/pause`, {
      userUuid,
      ...pauseData,
    });
  },

  /**
   * Resume simulation schedule
   * @param {string} userUuid - The UUID of the user
   * @returns {Promise} - Promise that resolves to the API response
   */
  resumeSchedule: async (userUuid) => {
    return APIService.post(`/api/v1/simulation/schedule/resume/${userUuid}`);
  },
};

export default SimulationScheduleAPI;
